import { useEffect, type CSSProperties, type ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { Spinner } from '@/components/feedback/Spinner'

interface ProtectedRouteProps {
  children: ReactNode
}

const loadingContainerStyles: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '100vh',
  backgroundColor: 'var(--color-background)',
}

/**
 * ProtectedRoute component that wraps protected routes.
 * - Redirects unauthenticated users to /login
 * - Redirects users without active subscription to /subscribe
 * - Shows loading spinner during auth/subscription initialization
 */
export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, isLoading, isInitialized, subscription, isSubscriptionLoading, fetchSubscription } =
    useAuthStore()
  const location = useLocation()

  // Fetch subscription when user is authenticated
  useEffect(() => {
    if (user && !subscription && !isSubscriptionLoading) {
      void fetchSubscription()
    }
  }, [user, subscription, isSubscriptionLoading, fetchSubscription])

  console.log('[ProtectedRoute] Checking auth:', {
    user: user?.email || 'null',
    isLoading,
    isInitialized,
    subscription: subscription?.has_access,
    isSubscriptionLoading,
    pathname: location.pathname,
  })

  // Show loading state while auth is initializing
  if (!isInitialized || isLoading) {
    console.log('[ProtectedRoute] Showing loading state (auth)')
    return (
      <div style={loadingContainerStyles}>
        <Spinner size="lg" />
      </div>
    )
  }

  // If not authenticated, redirect to login page
  if (!user) {
    console.log('[ProtectedRoute] No user, redirecting to /login from', location.pathname)
    // Save the intended destination for redirect after login
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  // Show loading state while subscription is loading
  if (!subscription || isSubscriptionLoading) {
    console.log('[ProtectedRoute] Showing loading state (subscription)')
    return (
      <div style={loadingContainerStyles}>
        <Spinner size="lg" />
      </div>
    )
  }

  // If no subscription access, redirect to subscribe page
  if (!subscription.has_access) {
    console.log(
      '[ProtectedRoute] No subscription, redirecting to /subscribe from',
      location.pathname
    )
    return <Navigate to="/subscribe" state={{ from: location }} replace />
  }

  console.log('[ProtectedRoute] User authenticated with subscription, rendering children')
  // User is authenticated with active subscription, render children
  return <>{children}</>
}
